import { Interface as WasmInterface } from "svelte-pixel-editor-wasm";
import { valueWritable } from "@ceyper-common";
import { File } from "./file";
import { palette } from "$lib/image/palette";
import { wasm_event_callback } from "$lib/wasm/wasm";
import { COMMANDS } from "$lib/commands";
import { createEvent } from "$lib/utility/event";

export type ToolOptions = Map<string, ToolOption>;
export type ToolOption =
    | {
          type: "Number";
          value: number;
          min: number;
          max: number;
      }
    | {
          type: "Bool";
          value: boolean;
      };

export class Interaction {
    #int: WasmInterface;

    get internal() {
        return this.#int;
    }

    file: File = null!;

    frameIndex = valueWritable(-1);
    layerIndex = valueWritable(-1);

    get tools() {
        return this.#int.get_tools();
    }

    get tool() {
        return this.#int.get_active_tool();
    }

    constructor() {
        this.#int = WasmInterface.new();

        palette.primary.subscribe((color) => {
            this.#int.set_active_color(color);
        });

        this.#initWasmEvents();
        (window as any).interface = this;
    }

    /// Events //////////////////////////////////
    readonly onToolChanged = createEvent<number>("tool-changed");
    readonly onCanvasDataChanged = createEvent<number>("canvas-data-changed");
    readonly onDataChanged = createEvent<void>("data-changed");

    #wasmEvents = {
        "frame-changed": (frame) => this.frameIndex.set(frame),
        "layer-changed": (layer) => this.layerIndex.set(layer),
        undo: this.onDataChanged.trigger,
        redo: this.onDataChanged.trigger,
        "color-changed": (color) => {
            let index = palette.active.value.colors.indexOf(color);
            palette.index.set(index);
            palette.primary.set(color);
        },
        "canvas-data-changed": this.onCanvasDataChanged.trigger.bind(this),
    } as Record<string, ((data: any) => void) | undefined>;

    #initWasmEvents() {
        wasm_event_callback.interaction = (event: string, data: any) => {
            setTimeout(() => {
                const ev = this.#wasmEvents[event];
                if (!ev) {
                    return console.error(`Unknown event interaction::${event}`);
                }
                ev(data);
            });
        };
    }

    registerCommandHandlers() {
        const { add, destroy } = COMMANDS.registerActions("all");

        this.tools.forEach((tool: string, index: number) => {
            add(`editor.tool.${tool as "pencil"}`, () => {
                this.#int.set_active_tool(index);
                this.onToolChanged.trigger(index);
            });
        });
        return destroy;
    }

    setFile(file: File) {
        this.#int.set_active_file(file.internal);
        this.file = file;
        this.layerIndex.set(0);
        this.frameIndex.set(0);
        (window as any).file = file;
    }

    clearFile() {
        // this.#int.clear_file(); // todo
        this.file = null!;
        this.layerIndex.set(0);
    }

    setFrame(frame: number) {
        this.#int.set_active(frame, this.layerIndex.value);
    }

    setLayer(layer: number) {
        this.layerIndex.set(layer);
        this.#int.set_active(this.frameIndex.value, this.layerIndex.value);
    }

    getIsCurrentToolActive() {
        return this.#int.get_is_current_tool_active() as boolean;
    }

    setToolOptions(options: ToolOptions) {
        this.#int.set_active_options(options);
    }

    getToolOptions() {
        return this.#int.get_active_options() as ToolOptions;
    }

    pointerDown(pos: Vec2, info: ModifierInfo) {
        this.#int.on_pointer_down(pos.x, pos.y, pointerModifiers(info));
    }

    pointerMove(pos: Vec2, info: ModifierInfo) {
        this.#int.on_pointer_move(pos.x, pos.y, pointerModifiers(info));
    }

    pointerUp(pos: Vec2, info: ModifierInfo) {
        this.#int.on_pointer_up(pos.x, pos.y, pointerModifiers(info));
    }

    pointerCancel() {
        this.#int.on_pointer_cancel();
    }

    redraw() {
        // console.error("redraw");
    }

    /// FILE OPERATIONS

    addLayer(frame: number, layer: number) {
        this.#int.add_layer_to_frame(frame, layer);
    }

    duplicateLayer(frame: number, layer: number) {
        this.#int.duplicate_layer_in_frame(frame, layer);
    }

    removeLayer(frame: number, layer: number) {
        this.#int.remove_layer_from_frame(frame, layer);
    }

    moveLayer(frame: number, from: number, to: number) {
        this.#int.move_layer_in_frame(frame, from, to);
    }

    mergeLayer(frame: number, from: number) {
        this.#int.merge_layers_in_frame(frame, from);
    }

    renameLayer(frame: number, layer: number, name: string) {
        this.#int.rename_layer_in_frame(frame, layer, name);
    }

    setLayerVisibility(frame: number, layer: number, visible: boolean) {
        this.#int.set_layer_visible_in_frame(frame, layer, visible);
    }

    setFrameDuration(frame: number, duration: number) {
        this.#int.set_frame_duration(frame, duration);
    }

    setAllFrameDurations(duration: number) {
        this.#int.set_all_frames_duration(duration);
    }

    addFrame(delay = 100, frameIndex: number) {
        this.#int.add_frame(frameIndex);
    }

    duplicateFrame(frameIndex: number) {
        this.#int.duplicate_frame(frameIndex);
    }

    removeFrame(frameIndex: number) {
        this.#int.remove_frame(frameIndex);
    }

    moveFrame(from: number, to: number) {
        this.#int.move_frame(from, to);
    }

    undo() {
        this.#int.undo();
    }

    redo() {
        this.#int.redo();
    }
}

type ExecutionContext = {
    setPixel: (x: number, y: number, color: number) => void;
    getPixel: (x: number, y: number) => number;
    blendPixel: (x: number, y: number, color: number) => void;
};
type Executable = (context: ExecutionContext) => boolean;

type ModifierInfo = { ctrl: boolean; alt: boolean; shift: boolean };

function pointerModifiers(info: ModifierInfo) {
    return 0 | (info.ctrl ? 1 : 0) | (info.alt ? 2 : 0) | (info.shift ? 4 : 0);
}
